import { getScopedDomain } from '../lib/tenantScope';
// Call Recordings — 3CX-shape unified list. Toolbar drives bulk operations on
// selected rows (Download, Transcribe, Delete, Archive) plus a range-based
// Purge and a Settings modal. Filter chips: Calls / Range / Location.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Download, Mic, Trash2, Archive, FileX, Settings as Cog,
  Filter, Search, X,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { nextSelectAll } from '../lib/bulkSelect';
import { ConfirmDialog } from '../components/Dialog';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });
// recording_url points at /api/recordings/audio/<uuid>; plain <a href> can't
// carry the Bearer header, so playback/download URLs append ?token= (the path
// is on the API's allowQueryToken list).
const audioUrl = (u?: string) => (u ? `${u}${u.includes('?') ? '&' : '?'}token=${encodeURIComponent(getToken() || '')}` : '');
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...auth(), ...(init.headers || {}) },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

type CallsFilter    = 'All' | 'Inbound' | 'Outbound' | 'Internal';
type RangeFilter    = 'Today' | 'Yesterday' | 'Last 7 days' | 'Last 30 days' | 'Custom';
type LocationFilter = 'All Recordings' | 'Onsite' | 'Archive';

type Row = {
  recording_id: string;
  recording_url?: string;
  start_time: string;
  end_time?: string;
  domain_uuid: string;
  archived?: boolean;
  transcribed?: boolean;
  transcribe_queued?: boolean;
  transcription?: string;
  sentiment_score?: number | null;
  caller_dn?: string;     caller_number?: string;     caller_name?: string;
  callee_dn?: string;     callee_number?: string;     callee_name?: string;
  display_name?: string;  did?: string;               department?: string;
};

export default function CallRecordings() {
  const me = api.me();
  const qc = useQueryClient();

  // Filter state
  const [calls,    setCalls]    = useState<CallsFilter>('All');
  const [range,    setRange]    = useState<RangeFilter>('Today');
  const [location, setLocation] = useState<LocationFilter>('All Recordings');
  const [search,   setSearch]   = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingCalls,    setPendingCalls]    = useState<CallsFilter>('All');
  const [pendingRange,    setPendingRange]    = useState<RangeFilter>('Today');
  const [pendingLocation, setPendingLocation] = useState<LocationFilter>('All Recordings');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');

  // Selection + paging
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [purgeOpen, setPurgeOpen]       = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [from, to] = useMemo(() => rangeToDates(range, customFrom, customTo), [range, customFrom, customTo]);

  // Reset page when filters change
  useEffect(() => { setPage(1); setSelected(new Set()); }, [calls, range, location, search, pageSize, from, to]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (getScopedDomain(me?.domain_uuid)) p.set('domain_uuid', getScopedDomain(me?.domain_uuid));
    if (from) p.set('from_date', from);
    if (to)   p.set('to_date',   to);
    if (search) p.set('search', search);
    if (calls    !== 'All')            p.set('direction', calls.toLowerCase());
    if (location !== 'All Recordings') p.set('location',  location.toLowerCase());
    p.set('limit', '500');
    return p.toString();
  }, [getScopedDomain(me?.domain_uuid), from, to, search, calls, location]);

  const { data: list = [] } = useQuery<Row[]>({
    queryKey: ['recordings', params],
    queryFn:  () => fJson(`/api/recordings?${params}`),
    enabled:  !!getScopedDomain(me?.domain_uuid),
  });

  // Client-side pagination on the fetched window
  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const offset = (page - 1) * pageSize;
  const visible = list.slice(offset, offset + pageSize);

  // ── Bulk operations ─────────────────────────────────────────────────────────
  // domain_uuid rides along for super-admin department scoping; the server pins
  // non-super callers to their own department regardless (#473/#1045).
  const bulkBody = (ids: string[]) => JSON.stringify({ ids, domain_uuid: getScopedDomain(me?.domain_uuid) });
  const archive    = useMutation({ mutationFn: (ids: string[]) => fJson('/api/recordings/bulk-archive',    { method: 'POST', body: bulkBody(ids) }), onSuccess });
  const remove     = useMutation({ mutationFn: (ids: string[]) => fJson('/api/recordings/bulk-delete',     { method: 'POST', body: bulkBody(ids) }), onSuccess });
  const transcribe = useMutation({ mutationFn: (ids: string[]) => fJson('/api/recordings/bulk-transcribe', { method: 'POST', body: bulkBody(ids) }), onSuccess });
  function onSuccess() {
    qc.invalidateQueries({ queryKey: ['recordings'] });
    setSelected(new Set());
  }

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    // Select all spans the WHOLE filtered list, not just the visible page (#8).
    setSelected(nextSelectAll(selected.size, list.map(r => r.recording_id)));
  }

  function downloadSelected() {
    const rows = list.filter(r => selected.has(r.recording_id) && r.recording_url);
    rows.forEach(r => {
      const a = document.createElement('a');
      a.href = audioUrl(r.recording_url);
      a.download = `${r.recording_id}.wav`;
      a.click();
    });
  }
  function ensureSel(action: string): boolean {
    if (!selected.size) { alert(`Select rows to ${action}.`); return false; }
    return true;
  }

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Recordings</h1>

      {/* Toolbar — primary blue 6 buttons (3CX shape) */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => ensureSel('download') && downloadSelected()}>
          <Download size={14} /> Download
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => ensureSel('transcribe') && transcribe.mutate([...selected])}>
          <Mic size={14} /> Transcribe
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => ensureSel('delete') && setConfirmDelete(true)}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => ensureSel('archive') && archive.mutate([...selected])}>
          <Archive size={14} /> Archive
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => setPurgeOpen(true)}>
          <FileX size={14} /> Purge
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => setSettingsOpen(true)}>
          <Cog size={14} /> Settings
        </button>
      </div>

      {/* Filter chips row */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip label={`Calls: ${calls}`} onClick={() => openFilter('calls')} />
        <FilterChip label={`Range: ${range}`} onClick={() => openFilter('range')} />
        <FilterChip label={`Location: ${location}`} onClick={() => openFilter('location')} />
        <button className="btn-ghost p-1.5" onClick={() => openFilter('all')} title="Filter">
          <Filter size={14} />
        </button>
        <div className="flex-1 min-w-48 flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search"
            className="bg-transparent outline-none text-sm w-full"
          />
        </div>
      </div>

      {filterOpen && (
        <div className="card p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Calls">
              <select className="input" value={pendingCalls} onChange={e => setPendingCalls(e.target.value as CallsFilter)}>
                {(['All','Inbound','Outbound','Internal'] as CallsFilter[]).map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Range">
              <select className="input" value={pendingRange} onChange={e => setPendingRange(e.target.value as RangeFilter)}>
                {(['Today','Yesterday','Last 7 days','Last 30 days','Custom'] as RangeFilter[]).map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select className="input" value={pendingLocation} onChange={e => setPendingLocation(e.target.value as LocationFilter)}>
                {(['All Recordings','Onsite','Archive'] as LocationFilter[]).map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
            {pendingRange === 'Custom' && (
              <>
                <Field label="From"><input className="input" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></Field>
                <Field label="To"><input className="input" type="date" value={customTo}   onChange={e => setCustomTo(e.target.value)} /></Field>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm inline-flex items-center gap-1"
                    onClick={() => {
                      setCalls(pendingCalls); setRange(pendingRange); setLocation(pendingLocation);
                      setFilterOpen(false);
                    }}>
              <Search size={14} /> Search
            </button>
            <button className="btn-ghost text-sm inline-flex items-center gap-1" onClick={() => setFilterOpen(false)}>
              <X size={12} /> Close
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={list.length > 0 && selected.size === list.length}
                       onChange={toggleAll} />
              </th>
              <th className="w-20">ID</th>
              <th className="w-40">Date</th>
              <th>Display Name</th>
              <th>DID</th>
              <th>Caller</th>
              <th>Callee</th>
              <th className="w-24">Sentiment</th>
              <th className="w-32">Transcription</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <Recording key={r.recording_id} r={r}
                         selected={selected.has(r.recording_id)}
                         onToggle={() => toggle(r.recording_id)} />
            ))}
            {!visible.length && (
              <tr><td colSpan={10} className="text-center py-8 text-textSecondary">No recordings.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={pages} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />

      {purgeOpen    && <PurgeModal    onClose={() => setPurgeOpen(false)} />}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}

      {/* Destructive confirm — non-dismissable (#888): backdrop/Escape can't close it */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate([...selected])}
        title="Delete recordings"
        message={`Permanently delete ${selected.size} recording(s)? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        dismissable={false}
      />
    </div>
  );

  function openFilter(_which: string) {
    setPendingCalls(calls); setPendingRange(range); setPendingLocation(location);
    setFilterOpen(true);
  }
}

function FilterChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
            className="bg-primary text-white text-xs rounded-md px-2.5 py-1.5 hover:bg-primary/90">
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label className="block text-xs text-textSecondary mb-1">{label}</label>
      {children}
    </div>
  );
}

function Recording({ r, selected, onToggle }: { r: Row; selected: boolean; onToggle: () => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        className={`hover:bg-elevated cursor-pointer ${selected ? 'bg-primary/5' : ''}`}
        onClick={() => setExpanded(e => !e)}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={onToggle} />
        </td>
        <td className="text-sm text-primary">{shortId(r.recording_id)}</td>
        <td className="text-sm">{formatStamp(r.start_time)}</td>
        <td className="text-sm">{r.display_name || r.caller_name || r.callee_name || ''}</td>
        <td className="font-mono text-sm">{r.did || ''}</td>
        <td className="font-mono text-sm">
          {r.caller_number || r.caller_dn || ''}
          {r.caller_name && <div className="text-xs text-textSecondary">{r.caller_name}</div>}
        </td>
        <td className="font-mono text-sm">
          {r.callee_number || r.callee_dn || ''}
          {r.callee_name && <div className="text-xs text-textSecondary">{r.callee_name}</div>}
        </td>
        <td><SentimentChip score={r.sentiment_score} /></td>
        <td className="text-sm">
          {r.transcribed ? <span className="badge bg-success/20 text-success">done</span>
           : r.transcribe_queued ? <span className="badge bg-warn/20 text-warn">queued</span>
           : <span className="text-textSecondary">—</span>}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          {r.recording_url && (
            <a href={audioUrl(r.recording_url)} download
               className="btn-ghost p-1 inline-flex" title="Download"
               onClick={(e) => e.stopPropagation()}>
              <Download size={14} />
            </a>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-elevated/50">
          <td colSpan={10} className="px-4 py-3 space-y-2">
            {r.recording_url
              ? <audio controls autoPlay src={audioUrl(r.recording_url)} className="w-full h-9" />
              : <p className="text-sm text-textSecondary italic">No audio file available.</p>}
            {r.transcribed && r.transcription
              ? <p className="text-sm text-textSecondary whitespace-pre-wrap leading-relaxed">{r.transcription}</p>
              : r.transcribed
              ? <p className="text-sm text-textSecondary italic">Transcription text not available.</p>
              : r.transcribe_queued
              ? <p className="text-sm text-textSecondary italic">Transcription queued…</p>
              : <p className="text-sm text-textSecondary italic">Not transcribed.</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function SentimentChip({ score }: { score: number | null | undefined }) {
  if (score == null) return <span className="text-textSecondary">—</span>;
  const cls = score > 25 ? 'bg-success/20 text-success'
            : score < -25 ? 'bg-danger/20 text-danger'
            : 'bg-warn/20 text-warn';
  return <span className={`badge ${cls}`}>{score}</span>;
}

function shortId(id: string): string {
  if (!id) return '';
  // Recording ids are uuids; return last 6 hex chars as a stable short id.
  return id.length > 8 ? id.slice(-6).toUpperCase() : id;
}

function formatStamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${String(h).padStart(2,'0')}:${min} ${ap}`;
}

function rangeToDates(r: RangeFilter, customFrom: string, customTo: string): [string, string] {
  const now = new Date();
  const startOfDay = (d: Date) => { d.setHours(0,0,0,0); return d; };
  const iso = (d: Date) => d.toISOString();
  if (r === 'Today') {
    const s = startOfDay(new Date(now)); return [iso(s), iso(now)];
  }
  if (r === 'Yesterday') {
    const s = startOfDay(new Date(now)); s.setDate(s.getDate() - 1);
    const e = new Date(s); e.setDate(e.getDate() + 1);
    return [iso(s), iso(e)];
  }
  if (r === 'Last 7 days') {
    const s = new Date(now); s.setDate(s.getDate() - 7); return [iso(s), iso(now)];
  }
  if (r === 'Last 30 days') {
    const s = new Date(now); s.setDate(s.getDate() - 30); return [iso(s), iso(now)];
  }
  if (r === 'Custom') {
    return [customFrom || '', customTo || ''];
  }
  return ['', ''];
}

function Pagination({
  page, pages, pageSize, onPage, onPageSize,
}: {
  page: number; pages: number; pageSize: number;
  onPage: (n: number) => void; onPageSize: (n: number) => void;
}) {
  function pageNumbers(): (number | '…')[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | '…')[] = [1];
    const start = Math.max(2, page - 1);
    const end   = Math.min(pages - 1, page + 1);
    if (start > 2) out.push('…');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < pages - 1) out.push('…');
    out.push(pages);
    return out;
  }
  const box = 'px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-elevated';
  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <button className={`${box} disabled:opacity-40`}
              disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      {pageNumbers().map((n, i) =>
        n === '…' ? <span key={i} className="px-2 text-textSecondary">…</span>
        : <button key={i}
                  className={n === page
                    ? 'px-3 py-1.5 rounded-md text-sm bg-primary text-white border border-primary'
                    : box}
                  onClick={() => onPage(n)}>{n}</button>
      )}
      <button className={`${box} disabled:opacity-40`}
              disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      <select className="input w-20 ml-2 text-sm" value={pageSize}
              onChange={e => onPageSize(Number(e.target.value))}>
        {[10, 25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function PurgeModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!from || !to) { setErr('Pick both dates'); return; }
    if (!confirm(`Permanently delete recordings from ${from} to ${to}? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      await fJson('/api/recordings/purge', { method: 'POST', body: JSON.stringify({ from, to }) });
      onClose();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">Purge recordings</h2>
        <p className="text-sm text-textSecondary mb-3">
          Permanently delete recordings in the selected range. This cannot be undone.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Field label="From"><input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></Field>
          <Field label="To">  <input className="input" type="date" value={to}   onChange={e => setTo(e.target.value)} /></Field>
        </div>
        {err && <div className="text-sm text-danger mb-2">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-danger" disabled={busy} onClick={go}>{busy ? 'Purging…' : 'Purge'}</button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const settings = useQuery<any>({ queryKey: ['recordings-settings'], queryFn: () => fJson('/api/recordings/settings') });

  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [retentionDays,    setRetentionDays]    = useState(90);
  const [autoArchiveDays,  setAutoArchiveDays]  = useState(30);
  const [autoTranscribe,   setAutoTranscribe]   = useState(false);
  const [recordInbound,    setRecordInbound]    = useState(true);
  const [recordOutbound,   setRecordOutbound]   = useState(true);
  const [recordInternal,   setRecordInternal]   = useState(false);
  const [consentAnnounce,  setConsentAnnounce]  = useState(false);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setRecordingEnabled(!!s.recording_enabled);
    setRetentionDays(s.retention_days ?? 90);
    setAutoArchiveDays(s.auto_archive_days ?? 30);
    setAutoTranscribe(!!s.auto_transcribe);
    setRecordInbound(s.record_inbound !== false);
    setRecordOutbound(s.record_outbound !== false);
    setRecordInternal(!!s.record_internal);
    setConsentAnnounce(!!s.consent_announce);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/recordings/settings', {
      method: 'PUT',
      body: JSON.stringify({
        recording_enabled: recordingEnabled,
        retention_days: retentionDays,
        auto_archive_days: autoArchiveDays,
        auto_transcribe: autoTranscribe,
        record_inbound: recordInbound,
        record_outbound: recordOutbound,
        record_internal: recordInternal,
        consent_announce: consentAnnounce,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recordings-settings'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">Recording Settings</h2>
        <label className="flex items-start gap-2 text-sm border border-border rounded-md p-3 mb-3">
          <input type="checkbox" className="mt-0.5" checked={recordingEnabled}
                 onChange={e => setRecordingEnabled(e.target.checked)} />
          <span>
            <span className="font-medium">Enable call recording for this department</span>
            <span className="block text-xs text-textSecondary mt-0.5">
              Master switch. Calls record only when this is on AND the user has Call Recording
              enabled (Users → Options). Turning it off disarms recording for everyone here.
            </span>
          </span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Retention days">
            <input className="input" type="number" min={1} value={retentionDays}
                   onChange={e => setRetentionDays(Number(e.target.value || 0))} />
          </Field>
          <Field label="Auto-archive after (days)">
            <input className="input" type="number" min={1} value={autoArchiveDays}
                   onChange={e => setAutoArchiveDays(Number(e.target.value || 0))} />
          </Field>
        </div>
        <div className="flex flex-col gap-2 mt-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={autoTranscribe}
                   onChange={e => setAutoTranscribe(e.target.checked)} />
            Auto-transcribe new recordings
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={recordInbound}
                   onChange={e => setRecordInbound(e.target.checked)} />
            Record inbound calls
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={recordOutbound}
                   onChange={e => setRecordOutbound(e.target.checked)} />
            Record outbound calls
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={recordInternal}
                   onChange={e => setRecordInternal(e.target.checked)} />
            Record internal calls
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={consentAnnounce}
                   onChange={e => setConsentAnnounce(e.target.checked)} />
            <span>
              Play a consent announcement on recorded calls
              <span className="block text-xs text-textSecondary mt-0.5">
                Callers hear &quot;this call may be monitored or recorded&quot; at the start of
                calls that will be recorded. Required in two-party-consent states.
              </span>
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
